import { useQuery } from '@tanstack/react-query'

import { catalogosApi } from '../api/catalogos'
import { chaves } from '../api/chaves'
import { equipesApi, unidadesApi } from '../api/organizacao'
import type { ItemCatalogo, NomeCatalogo } from '@baiocchi/shared'

/**
 * Catálogos ativos para preencher select.
 *
 * `retry: false` e falha silenciosa: nenhuma destas rotas existe na API ainda, e
 * um formulário que mostra seis erros de rede porque os selects não carregaram é
 * inutilizável. O campo aparece vazio, com a dica de que a lista não chegou — o
 * submit ainda é validado pelo servidor.
 *
 * `staleTime` longo porque catálogo muda por decisão administrativa, não pelo uso.
 */
const OPCOES_CATALOGO = {
  retry: false,
  staleTime: 10 * 60_000,
  gcTime: 30 * 60_000,
} as const

export function useCatalogoAtivo(catalogo: NomeCatalogo) {
  return useQuery({
    queryKey: chaves.catalogos.ativos(catalogo),
    queryFn: ({ signal }) => catalogosApi.ativos(catalogo, signal),
    ...OPCOES_CATALOGO,
  })
}

export function useUnidades() {
  return useQuery({
    queryKey: chaves.organizacao.unidades({ ativo: 'true', limite: 200 }),
    queryFn: ({ signal }) => unidadesApi.listar({ ativo: 'true', limite: 200 }, signal),
    ...OPCOES_CATALOGO,
  })
}

export function useEquipes(unidadeId?: string | number | null) {
  const filtros = { ativo: 'true', limite: 200, ...(unidadeId ? { unidadeId } : {}) }
  return useQuery({
    queryKey: chaves.organizacao.equipes(filtros),
    queryFn: ({ signal }) => equipesApi.listar(filtros, signal),
    ...OPCOES_CATALOGO,
  })
}

/**
 * Atendentes elegíveis de uma equipe (RN-14).
 *
 * A regra de quem pode ser responsável — ativo, membro da equipe, papel elegível,
 * e `receberLeads` quando é para distribuição — é **do servidor**. A tela pede a
 * lista pronta em vez de filtrar `GET /usuarios` por conta própria: um filtro
 * client-side aqui viraria a segunda definição de RN-14, e é a que o operador
 * enxergaria.
 */
export function useAtendentesElegiveis(
  equipeId: number | null,
  paraDistribuicao = false,
) {
  return useQuery({
    queryKey: chaves.organizacao.atendentesElegiveis(equipeId, paraDistribuicao),
    queryFn: ({ signal }) =>
      equipesApi.atendentesElegiveis(equipeId as number, paraDistribuicao, signal),
    enabled: equipeId !== null,
    retry: false,
    staleTime: 60_000,
  })
}

/**
 * Dica do campo quando a lista não chegou.
 *
 * Um select vazio sem explicação faz o operador procurar o erro no próprio
 * cadastro ("por que não tem nenhuma unidade?"). Dizer qual endpoint faltou
 * transforma isso em informação útil — e em item de integração.
 */
export function dicaSeIndisponivel(
  consulta: { isError: boolean; isPending: boolean },
  endpoint: string,
  dicaNormal?: string,
): string | undefined {
  if (consulta.isError) return `Lista indisponível: ${endpoint} ainda não foi publicado pela API.`
  if (consulta.isPending) return 'Carregando opções…'
  return dicaNormal
}

/** Opções de `<Selecao>` a partir de um catálogo, já ordenadas pelo servidor. */
export function opcoesDeCatalogo(
  itens: ReadonlyArray<ItemCatalogo> | undefined,
): Array<{ valor: string; rotulo: string }> {
  return (itens ?? []).map((item) => ({ valor: String(item.id), rotulo: item.titulo }))
}

export function opcoesDeReferencia(
  itens: ReadonlyArray<{ id: number; titulo: string }> | undefined,
): Array<{ valor: string; rotulo: string }> {
  return (itens ?? []).map((item) => ({ valor: String(item.id), rotulo: item.titulo }))
}

export function opcoesDePessoas(
  itens: ReadonlyArray<{ uuid: string; nome: string }> | undefined,
): Array<{ valor: string; rotulo: string }> {
  return (itens ?? []).map((item) => ({ valor: item.uuid, rotulo: item.nome }))
}
